import React, { useState, useEffect } from 'react'
import { isBoolean, isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import {
  TextField,
  Box,
  MenuItem,
  Typography,
  Grid,
  Button,
  makeStyles,
  Tooltip,
} from '@material-ui/core'
import {
  FormContext,
  useForm,
  Controller,
  useFieldArray,
} from 'react-hook-form'

import { PlusCircle as PlusCircleIcon, Info as InfoIcon } from 'react-feather'

import { Label, SelectCountry, Permitted } from 'components'
import { SharedCollectedFieldsModal, TableSharedCollectedFields } from './'

import DataProcessTemplateForm from '../DataProcessTemplateForm'
import SharedSoftwareForm from '../SharedSoftwareForm'

import useAuth from 'hooks/useAuth'
import useDataProcessOptions from 'hooks/useDataProcessOptions'
import useDataProcessTemplateQuestion from 'hooks/useDataProcessTemplateQuestion'
import useFetch from 'hooks/useFetch'
import useSelectCountry from 'hooks/useSelectCountry'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import styles from './styles'
import theme from 'theme'

const useStyles = makeStyles(styles)

const NewForm = ({ mode, submitSuccess, setLoading, ...props }) => {
  const { convertFormDataToArray } = helpers.functions

  const [dataTreatment, setDataTreatment] = useState(props?.dataTreatment)
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [pendingSharedCollectedFields, setPendingSharedCollectedFields] =
    useState([])

  const isCreationMode = !dataTreatment?.id

  const {
    response: sharedDataResponse,
    refresh: refreshSharedCollectedFields,
    isLoading,
  } = useFetch(
    service.dponet.sharedCollectedFields.list,
    {
      dataProcessId: dataTreatment.dataProcessId,
      dataTreatmentId: dataTreatment.id,
    },
    [dataTreatment.dataProcessId, dataTreatment.id],
    !!dataTreatment.dataProcessId && !!dataTreatment.id && !isCreationMode,
  )

  const existingSharedCollectedFields =
    sharedDataResponse?.data?.sharedCollectedFields || []

  const sharedCollectedFields = isCreationMode
    ? pendingSharedCollectedFields
    : existingSharedCollectedFields

  const { company } = useAuth()
  const classes = useStyles()
  const {
    selectedCountries,
    setSelectedCountries,
    manySelectedCountries,
    setManySelectedCountries,
    sortNewNamesToObject,
  } = useSelectCountry()

  const {
    triggerValidation: triggerTemplateValidation,
    getValues: getTemplateValues,
  } = useDataProcessTemplateQuestion()

  const onlyShow = mode === 'show'

  const isRequiredField =
    company?.kind !== constants.userCompanies.KIND_TEMPLATE

  const fieldTreatment = (field) => {
    if (isRequiredField && !isBoolean(field)) return 'false'
    if (isRequiredField && isBoolean(field)) return field?.toString()
    return field?.toString()
  }

  const formMethods = useForm({
    defaultValues: {
      dataProcessId: dataTreatment.dataProcessId || '',
      dataTreatmentId: dataTreatment.id || '',
      shareProcessId:
        helpers.functions.dig(dataTreatment.shareProcess, 'id') || '',
      shareTypeId: helpers.functions.dig(dataTreatment.shareType, 'id') || '',
      description: dataTreatment.description || '',
      controllMechanism: dataTreatment.controllMechanism || '',
      sharedInAnotherSoftware: fieldTreatment(
        dataTreatment?.sharedInAnotherSoftware,
      ),
      internationalTransferDataTreatment: fieldTreatment(
        dataTreatment?.haveInternationalTransfer,
      ),
      internationalReason: dataTreatment?.internationalReason || '',
    },
  })

  const {
    errors,
    watch,
    control,
    setValue,
    getValues,
    setError,
    triggerValidation: triggerFormValidation,
  } = formMethods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sharedSoftwares',
    keyName: 'key',
  })

  const snackbar = useSnackbar()
  const dataProcessOptions = useDataProcessOptions()

  const { options } = dataProcessOptions.data
  const { internalAllocationModes, shareProcesses } = options

  const isInternational = watch('internationalTransferDataTreatment') === 'true'

  const setInternationalTransfersCountries = (fields) => {
    let newObject = Object.assign({}, manySelectedCountries)

    for (let index in fields) {
      const sharedSoftware = fields[index]
      newObject[`sharedSoftwares-${index}`] =
        sharedSoftware.internationalTransfers
    }

    setManySelectedCountries(newObject)
  }

  const verifyInternationalTransfer = (data) => {
    if (
      data.internationalTransferDataTreatment === 'true' &&
      selectedCountries <= 0
    ) {
      setError(
        'internationalTransferDataTreatment',
        'required',
        'Escolha no mínimo um país caso a resposta seja "Sim".',
      )
      return false
    }

    return true
  }

  const handleAddPendingSharedCollectedField = (newField) => {
    setPendingSharedCollectedFields((prev) => [...prev, newField])
    setOpenCreateModal(false)
    snackbar.open({
      message: 'Dados compartilhados adicionados (serão salvos ao confirmar)',
      variant: 'success',
    })
  }

  useEffect(() => {
    if (mode === 'edit' || mode === 'show') {
      setSelectedCountries(dataTreatment?.internationalTransfers)
    } else {
      setSelectedCountries([])
      setManySelectedCountries({})
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (getValues('internationalTransferDataTreatment') === 'false') {
      setSelectedCountries([])
    }

    // eslint-disable-next-line
  }, [getValues('internationalTransferDataTreatment')])

  useEffect(() => {
    if (getValues('sharedInAnotherSoftware') === 'true') {
      if (!dataTreatment?.sharedSoftwares?.length && !fields.length) {
        return append({ id: '', name: '', softwareAllocationType: '' })
      }

      if (!fields.length) {
        setInternationalTransfersCountries(dataTreatment?.sharedSoftwares)
        return append(dataTreatment?.sharedSoftwares)
      }
    }
    if (getValues('sharedInAnotherSoftware') === 'false') {
      setManySelectedCountries({})
      remove()
    }
    // eslint-disable-next-line
  }, [dataTreatment, getValues('sharedInAnotherSoftware')])

  const handleRemoveCountrySharedSoftwares = async (sharedSoftware, index) => {
    let newManySelectedCountries = manySelectedCountries
    delete newManySelectedCountries[`sharedSoftwares-${index}`]

    newManySelectedCountries = sortNewNamesToObject(newManySelectedCountries)
    setManySelectedCountries(newManySelectedCountries)

    for (let index in sharedSoftware?.internationalTransfers) {
      const internationalTransfer =
        sharedSoftware?.internationalTransfers[index]

      await service.dponet.sharedSoftwares.internationalTransferDestroy({
        dataProcessId: dataTreatment?.dataProcessId,
        dataTreatmentId: dataTreatment?.id,
        sharedSoftwareId: sharedSoftware?.id,
        internationalTransferId: internationalTransfer?.id,
      })
    }
  }

  const onSubmit = async (dataTreatment, template) => {
    const dataProcessId = dataTreatment.dataProcessId
    const dataTreatmentId = dataTreatment.dataTreatmentId
    setLoading(true)

    const normalizedDataTreatment = convertFormDataToArray(
      dataTreatment,
      'sharedSoftwares',
    )

    try {
      let response = await submitRoute({
        dataProcessId,
        dataTreatmentId,
        dataTreatment: {
          ...normalizedDataTreatment,
          internationalTransferCountries: selectedCountries,
          dataProcessTemplateQuestions: template,
        },
      })
      await submitSharedSoftwares(
        normalizedDataTreatment?.sharedSoftwares,
        response?.data?.dataTreatment?.id,
        response?.data?.dataTreatment?.dataProcessId,
      )

      await submitPendingSharedCollectedFields(
        dataTreatment.pendingSharedCollectedFields,
        response?.data?.dataTreatment?.id,
        response?.data?.dataTreatment?.dataProcessId,
      )

      submitSuccess()

      dataProcessOptions.loadData()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error) ||
          'Ocorreu algum erro! Tente novamente!',
        variant: 'error',
      })
      setLoading(false)
    }
  }

  const submitSharedSoftwares = async (
    sharedSoftwares,
    dataTreatmentId,
    dataProcessId,
  ) => {
    if (isEmpty(sharedSoftwares)) return

    for (let index in sharedSoftwares) {
      let sharedSoftware = sharedSoftwares[index]
      if (!!sharedSoftware.id) {
        await service.dponet.sharedSoftwares.put({
          dataProcessId,
          dataTreatmentId,
          sharedSoftwareId: sharedSoftware.id,
          sharedSoftware: {
            ...sharedSoftware,
            international_transfer_countries:
              manySelectedCountries[`sharedSoftwares-${index}`],
          },
        })
      } else {
        await service.dponet.sharedSoftwares.create({
          dataProcessId,
          dataTreatmentId,
          sharedSoftware: {
            ...sharedSoftware,
            international_transfer_countries:
              manySelectedCountries[`sharedSoftwares-${index}`],
          },
        })
      }
    }
  }

  const submitPendingSharedCollectedFields = async (
    pendingSharedCollectedFields,
    dataTreatmentId,
    dataProcessId,
  ) => {
    if (!pendingSharedCollectedFields?.length) return

    for (const pendingField of pendingSharedCollectedFields) {
      const sharedCollectedFieldsIds = pendingField.collectedFields?.map(
        (field) => field.id,
      )

      if (sharedCollectedFieldsIds?.length) {
        await service.dponet.sharedCollectedFields.create({
          dataProcessId,
          dataTreatmentId,
          sharedCollectedField: {
            sharedCollectedFieldsIds,
          },
        })
      }
    }
  }

  const handleRemoveCountry = async (country) => {
    const internationalTransferCreated =
      dataTreatment?.internationalTransfers?.find(
        (internationalTransfer) =>
          internationalTransfer.country === country.country,
      )

    if (
      dataTreatment?.id &&
      dataTreatment?.dataProcessId &&
      internationalTransferCreated
    ) {
      await service.dponet.dataTreatments.internationalTransferDestroy({
        dataProcessId: dataTreatment?.dataProcessId,
        dataTreatmentId: dataTreatment?.id,
        internationalTransferId: internationalTransferCreated.id,
      })
    }
  }

  const submitRoute =
    mode === 'edit'
      ? service.dponet.dataTreatments.put
      : service.dponet.dataTreatments.create

  const addSharedSoftware = () => {
    append({ id: '', name: '', softwareAllocationType: '' })
  }

  const removeSharedSoftware = async (index, sharedSoftware) => {
    if (!!sharedSoftware?.id) {
      try {
        setLoading(true)
        await handleRemoveCountrySharedSoftwares(sharedSoftware, index)

        await service.dponet.sharedSoftwares.destroy({
          dataProcessId: dataTreatment.dataProcessId,
          dataTreatmentId: dataTreatment.id,
          sharedSoftwareId: sharedSoftware.id,
        })

        remove(index)

        // Se o meio de compartilhamento removido for o ultimo, define que nao existem meios de compartilhamentos
        if (fields.length === 1) {
          await service.dponet.dataTreatments.put({
            dataProcessId: dataTreatment.dataProcessId,
            dataTreatmentId: dataTreatment.id,
            dataTreatment: { sharedInAnotherSoftware: false },
          })
        }

        let response = await service.dponet.dataTreatments.get({
          dataProcessId: dataTreatment?.dataProcessId,
          dataTreatmentId: dataTreatment?.id,
        })

        setDataTreatment(response?.data?.dataTreatment)
        setLoading(false)

        snackbar.open({
          message: 'Meio de compartilhamento removido com sucesso!',
          variant: 'success',
        })

        setValue(
          'sharedInAnotherSoftware',
          response?.data?.dataTreatment?.sharedInAnotherSoftware?.toString() ||
            'false',
        )
      } catch {
        setLoading(false)
        snackbar.open({
          message:
            'Ocorreu um erro ao tentar remover o meio de compartilhamento! Por favor, tente novamente mais tarde.',
          variant: 'error',
        })
      }
    } else {
      remove(index)

      let newManySelectedCountries = manySelectedCountries
      delete newManySelectedCountries[`sharedSoftwares-${index}`]

      newManySelectedCountries = sortNewNamesToObject(newManySelectedCountries)
      setManySelectedCountries(newManySelectedCountries)

      // Se o meio de compartilhamento removido for o ultimo, define que nao existem meios de compartilhamentos
      if (fields.length === 1) {
        setValue('sharedInAnotherSoftware', 'false')
      }
      setLoading(false)
      snackbar.open({
        message: 'Meio de compartilhamento removido com sucesso!',
        variant: 'success',
      })
    }
  }

  const triggerValidation = async () => {
    const dataTreatmentValidation = await triggerFormValidation()
    const internationalTransferValidation = verifyInternationalTransfer(
      getValues(),
    )

    return dataTreatmentValidation && internationalTransferValidation
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const templateValidation = await triggerTemplateValidation()
    const generalInformationValidation = await triggerValidation()

    if (templateValidation && generalInformationValidation) {
      const formValues = getValues()
      if (isCreationMode) {
        formValues.pendingSharedCollectedFields = pendingSharedCollectedFields
      }
      onSubmit(formValues, getTemplateValues())
    }
  }

  return (
    <FormContext {...formMethods}>
      <form onSubmit={handleSubmit} id="shared-data-treatment-form">
        <Box py={2}>
          <Grid
            container
            justifyContent="flex-start"
            alignItems="center"
            spacing={3}
          >
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(3)}
              >
                <Controller as={<></>} name="dataProcessId" control={control} />
                <Controller
                  as={<></>}
                  name="dataTreatmentId"
                  control={control}
                />
                <Controller
                  as={
                    <TextField
                      label="Tipo de Compartilhamento"
                      select
                      color="primary"
                      variant="outlined"
                      disabled={onlyShow}
                      error={!!errors.shareTypeId}
                      helperText={errors?.shareTypeId && 'Preencha este campo'}
                      fullWidth
                    >
                      {internalAllocationModes.map((allocationMode) => (
                        <MenuItem
                          key={allocationMode.id}
                          value={allocationMode.id}
                        >
                          <Typography>{allocationMode.name}</Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                  control={control}
                  rules={{ required: true }}
                  name="shareTypeId"
                  mode="onBlur"
                />
                <Controller
                  as={
                    <TextField
                      label="Com quem o dado é compartilhado?"
                      select
                      disabled={onlyShow}
                      color="primary"
                      variant="outlined"
                      error={!!errors.shareProcessId}
                      helperText={
                        errors?.shareProcessId && 'Preencha este campo'
                      }
                      fullWidth
                    >
                      {shareProcesses &&
                        shareProcesses.map((shrProcess) => (
                          <MenuItem key={shrProcess.id} value={shrProcess.id}>
                            <Typography>{shrProcess.name}</Typography>
                          </MenuItem>
                        ))}
                      <MenuItem key={'another'} value={'another'}>
                        <Typography>Outro</Typography>
                      </MenuItem>
                    </TextField>
                  }
                  control={control}
                  rules={{ required: true }}
                  name="shareProcessId"
                  mode="onBlur"
                />
                {watch('shareProcessId') === 'another' && (
                  <Controller
                    as={
                      <TextField
                        label="Outro: com quem o dado é compartilhado?"
                        color="primary"
                        variant="outlined"
                        error={!!errors.anotherShareProcess}
                        helperText={
                          errors?.anotherShareProcess && 'Preencha este campo'
                        }
                        fullWidth
                      />
                    }
                    control={control}
                    rules={{ required: true }}
                    name="anotherShareProcess"
                    mode="onBlur"
                  />
                )}
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Controller
                as={
                  <TextField
                    label="Por quê o dado é compartilhado?"
                    color="primary"
                    variant="outlined"
                    disabled={onlyShow}
                    error={!!errors.description}
                    helperText={errors?.description && 'Preencha este campo'}
                    fullWidth
                  />
                }
                control={control}
                rules={{ required: true }}
                name="description"
                mode="onBlur"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(3)}
              >
                <Label
                  title="Este compartilhamento é uma transferência internacional?"
                  description={
                    <>
                      Caso a resposta for "Sim" selecione os países que é feito
                      o compartilhamento.{' '}
                      <Tooltip title="Ao selecionar esta opção, o compartilhamento é classificado como Externo, pois envolve o envio de dados para servidores que não pertencem à organização, o que impacta sua classificação.">
                        <InfoIcon size={14} />
                      </Tooltip>
                    </>
                  }
                  useTooltip={false}
                  item
                >
                  <Controller
                    as={
                      <TextField
                        select
                        color="primary"
                        variant="outlined"
                        disabled={onlyShow}
                        error={!!errors.internationalTransferDataTreatment}
                        helperText={
                          errors?.internationalTransferDataTreatment?.message
                        }
                        fullWidth
                      >
                        <MenuItem value="false">
                          <Typography>Não</Typography>
                        </MenuItem>
                        <MenuItem value="true">
                          <Typography>Sim</Typography>
                        </MenuItem>
                      </TextField>
                    }
                    control={control}
                    rules={{ required: isRequiredField }}
                    name="internationalTransferDataTreatment"
                    mode="onBlur"
                  />
                </Label>

                {isInternational && (
                  <Box
                    className={classes.internationalTransferField}
                    display="flex"
                    flexDirection="column"
                    gridGap={theme.spacing(2)}
                    p={2}
                  >
                    <Label description="Explique o motivo da transferência internacional">
                      <Controller
                        as={
                          <TextField
                            color="primary"
                            variant="outlined"
                            disabled={onlyShow}
                            error={!!errors.internationalReason}
                            helperText={
                              !!errors?.internationalReason &&
                              'Preencha este campo'
                            }
                            className={classes.inputBackground}
                            fullWidth
                          />
                        }
                        rules={{ required: true }}
                        control={control}
                        name="internationalReason"
                      />
                    </Label>
                    <SelectCountry
                      withTable
                      disabled={onlyShow}
                      label="Qual o país destinatário?"
                      handleRemoveCountry={handleRemoveCountry}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Controller
                as={
                  <TextField
                    label="Como o dado é compartilhado?"
                    color="primary"
                    variant="outlined"
                    disabled={onlyShow}
                    error={!!errors.controllMechanism}
                    helperText={
                      errors?.controllMechanism && 'Preencha este campo'
                    }
                    fullWidth
                  />
                }
                control={control}
                rules={{ required: true }}
                name="controllMechanism"
                mode="onBlur"
              />
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Label
                title="Para realizar este compartilhamento, você utiliza alguma plataforma, software ou aplicativo?"
                description={
                  <>
                    Caso a resposta for "Sim" liste os meios de
                    compartilhamentos de dados{' '}
                    <Tooltip title="Ao selecionar esta opção, o compartilhamento é classificado como Externo, pois envolve o envio de dados para servidores que não pertencem à organização, o que impacta sua classificação.">
                      <InfoIcon size={14} />
                    </Tooltip>
                  </>
                }
                useTooltip={false}
                item
              >
                <Controller
                  as={
                    <TextField
                      select
                      color="primary"
                      variant="outlined"
                      disabled={onlyShow}
                      error={!!errors.sharedInAnotherSoftware}
                      helperText={
                        errors?.sharedInAnotherSoftware && 'Preencha este campo'
                      }
                      fullWidth
                    >
                      <MenuItem value="false">
                        <Typography>Não</Typography>
                      </MenuItem>
                      <MenuItem value="true">
                        <Typography>Sim</Typography>
                      </MenuItem>
                    </TextField>
                  }
                  control={control}
                  rules={{ required: isRequiredField }}
                  name="sharedInAnotherSoftware"
                  mode="onBlur"
                />
              </Label>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              {watch('sharedInAnotherSoftware') === 'true' && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gridGap={theme.spacing(3)}
                >
                  {fields?.map((item, index) => (
                    <SharedSoftwareForm
                      sharedSoftware={item}
                      key={item.key}
                      index={index}
                      removeSharedSoftware={removeSharedSoftware}
                      disabled={onlyShow}
                    />
                  ))}
                  {!onlyShow && (
                    <Box display="flex" justifyContent="flex-end">
                      <Button variant="outlined" onClick={addSharedSoftware}>
                        Adicionar Meio de Compartilhamento
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(3)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">
                    Quais dados você compartilha?
                  </Typography>
                  {!onlyShow && (
                    <Permitted tag="create_data_process">
                      <Button
                        onClick={() => setOpenCreateModal(true)}
                        variant="outlined"
                        startIcon={<PlusCircleIcon size={20} />}
                        size="small"
                      >
                        Adicionar dados compartilhados
                      </Button>
                    </Permitted>
                  )}
                </Box>
                <SharedCollectedFieldsModal
                  open={openCreateModal}
                  onClose={() => setOpenCreateModal(false)}
                  dataProcessId={dataTreatment.dataProcessId}
                  dataTreatmentId={dataTreatment.id}
                  mode={isCreationMode ? 'create' : 'edit'}
                  deferSubmit={isCreationMode}
                  onSuccess={
                    isCreationMode
                      ? handleAddPendingSharedCollectedField
                      : refreshSharedCollectedFields
                  }
                />
                <TableSharedCollectedFields
                  edit={!onlyShow}
                  dataProcessId={dataTreatment.dataProcessId}
                  dataTreatmentId={dataTreatment.id}
                  sharedCollectedFields={sharedCollectedFields}
                  refresh={refreshSharedCollectedFields}
                  isLoading={isLoading}
                />
              </Box>
            </Grid>
          </Grid>
          <DataProcessTemplateForm step="data_treatment" disabled={onlyShow} />
        </Box>
      </form>
    </FormContext>
  )
}

NewForm.propTypes = {
  mode: PropTypes.string,
  submitSuccess: PropTypes.func,
  setLoading: PropTypes.func.isRequired,
}

export default NewForm
